"use client";

import React, { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  Users, Target, Eye, Briefcase, Home,
  UserCheck, Calendar, Newspaper, Mail,
  Phone, MapPin, Globe, Shield, GraduationCap,
  CheckCircle, AlertCircle, Loader2
} from "lucide-react";


import { Label } from "@/components/atoms/Label/label";
import Input from "@/components/atoms/Input/input";
import { Textarea } from "@/components/atoms/Textarea";
import useLocaleStore from "@/app/stores/useLocaleStore"
import { submitContactForm } from "@/actions/user/user.action";
import SmsConsentCard from "./consent";

type FormData = {
  name: string;
  email: string;
  subject: string;
  message: string;
};

export function AboutSection() {
  const language = useLocaleStore((state) => state.locale)
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<FormData>({
    defaultValues: {
      name: "",
      email: "",
      subject: "",
      message: "",
    }
  });
  const [isSuccess, setIsSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const sectionRef = useRef<HTMLFormElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const scrollToContact = () => {
      const element = document.getElementById("contact-form");
      if (!element) return;

      const offset = 100; // adjust for header spacing
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    };

    // Call on mount if hash already present
    if (window.location.hash === "#contact") {
      scrollToContact();
    }

    // Always call on hashchange
    const handleHashChange = () => {
      if (window.location.hash === "#contact") {
        scrollToContact();
      }
    };
    window.addEventListener("hashchange", handleHashChange);

    // Detect clicks on links to #contact (even if already on same page)
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const link = target.closest("a");
      if (link?.getAttribute("href")?.endsWith("#contact")) {
        scrollToContact();
      }
    };
    document.addEventListener("click", handleClick);

    // Intersection Observer for reveal animation
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
      document.removeEventListener("click", handleClick);
      observer.disconnect();
    };
  }, []);


  const onSubmit = async (data: FormData) => {
    setSubmitError(null);
    try {
      const result = await submitContactForm(data);
      if (result?.error) {
        setSubmitError(result.error);
        setIsSuccess(false);
      } else {
        setIsSuccess(true);
      }
    } catch (error) {
      setSubmitError(
        language === 'en'
          ? "An unexpected error occurred. Please try again."
          : "ስህተት ተከስቷል። እባክዎ ደግመው ይሞክሩ።"
      );
    }
  };

  if (typeof window !== 'undefined') {
    AOS.init({
      duration: 600,
      easing: 'ease-out-quad',
      once: true,
      offset: 100,
    });
  }

  const content = {
    en: {
      title: "About Habesha Network",
      subtitle: "Connecting the Ethiopian and Eritrean diaspora across North America through our community-driven platform.",
      missionTitle: "Our Mission",
      missionContent: "To easily connect all types of Ethiopian businesses, landlords and tenants, professional service providers, entrepreneurs, employers and job seekers, event programmers, news presenters, and others in North America with the rest of the Ethiopian community in our own language, and to elevate Ethiopian businesses from the confines of their locality to a national and international level.",
      visionTitle: "Our Vision",
      visionContent: "To empower the Habesha community through valuable connections and information exchange.",
      joinTitle: "Join Our Community",
      joinSubtitle: "Register now to access powerful tools for your business and community",
      shareTitle: "Share Valuable Information",
      shareSubtitle: "Help your community by sharing important news and resources",
      contactTitle: "Contact Us",
      contactSubtitle: "Have questions or suggestions? We'd love to hear from you!",
      contactInfo: "Contact Info",
      email: "Email",
      phone: "Phone",
      location: "Address",
      america: "Lavon, TX, USA",
      sendMessage: "Send Message",
      nameLabel: "Name *",
      emailLabel: "Email *",
      subjectLabel: "Subject *",
      messageLabel: "Message *",
      sending: "Sending...",
      successTitle: "Message Sent!",
      successMessage: "Thank you for contacting us. We'll get back to you soon.",
      anotherMessage: "Send Another Message",
      benefits: [
        { icon: <Briefcase className="w-6 h-6" />, title: "Promote Your Business", description: "Promote your business or buy with millions of members of our community across North America." },
        { icon: <UserCheck className="w-6 h-6" />, title: "Professional Services", description: "Connect with clients who need your knowledge and expertise, or find professionals who know your language and culture." },
        { icon: <Users className="w-6 h-6" />, title: "Hire From Community", description: "Hire or work with people who know your own language and culture." },
        { icon: <Home className="w-6 h-6" />, title: "Real Estate Solutions", description: "Rent, sell, or rent, buy your home to/from the Habesha community." },
        { icon: <Calendar className="w-6 h-6" />, title: "Event Promotion", description: "Reach your target audience by promoting programs, events, and community meetings on this website." },
        { icon: <Newspaper className="w-6 h-6" />, title: "Share Useful News", description: "Share useful and updated information with the community." }
      ],
      examples: [
        { icon: <Globe className="w-5 h-5" />, title: "Immigration Information", description: "Sharing current immigration policies and information" },
        { icon: <Shield className="w-5 h-5" />, title: "Safety Information", description: "Sharing important safety information and safety alerts to our community" },
        { icon: <GraduationCap className="w-5 h-5" />, title: "Entrepreneurship", description: "Introduce methods of entrepreneurship to our community" }
      ]
    },
    am: {
      title: "ሐበሻ ኔትዎርክ ምንድነው?",
      subtitle: "በሰሜን አሜሪካ የሚገኘውን የሐበሻውን ማበረሰብ ለማቀራረብ ለማገናኘትና በልዩ ሁኔታ የህዝቡን ፍላጎት በሚያሟላ መልኩ አገልግሎት የሚሰጥ በእኛው የሐበሻ ልጆች የተሰራ ዌብሳይት ነው።",
      missionTitle: "አላማችን",
      missionContent: "በሰሜን አሜሪካ የሚገኙ ማንኛውም አይነት የሀበሻ ቢዝነሶችን፣ ቤት አከራይና ተከራዮችን፣ ፕሮፌሽናል አገልግሎት ሰጪዎችን፣ ስራ ፈጣሪዎችን ፣ስራ ቀጣሪዎችንና ስራ ፈላጊዎችን ፣ፕሮግራም አዘጋጆችን ፣የዜና አቅራቢዎችንና ሌሎችንም ከቀረው የሀበሻ ማህበረሰብ ጋር በራሳችን ቋንቋ በቀላሉ ማገናኘትና የሐበሻውን ቢዝነስ ከቦታ ውስንነት በማውጣት ወደ አገር አቀፍ እና አለም አቀፍ ደረጃ ከፍ ማድረግ ነው።",
      visionTitle: "ራያችን",
      visionContent: "የሀበሻው ማህበረሰብ ጠቃሚ ኢንፎርሜሽኖችን በመለዋወጥና ራሱን በማበልፀግ እንዲሁም በልዩልዩ መልኩ በማሳደግ ተጽዕኖ ፈጣሪ እንዲሆን ማስቻል ነው።",
      joinTitle: "ማህበረሰባችንን ይቀላቀሉ",
      joinSubtitle: "ለራስዎና ለማህበረሰብዎ አቅም የሚሰጡ አገልግሎቶችን ለማግኘት አሁኑኑ ይመዝገቡ",
      shareTitle: "ጠቃሚ መረጃ ማጋራት",
      shareSubtitle: "ጠቃሚ ዜናዎችን እና መረጃዎችን በማጋራት ማህበረሰብዎን ይጥቀሙ",
      contactTitle: "አግኙን",
      contactSubtitle: "ጥያቄዎች ወይም ሐሳቦች አሉዎት? ከእርስዎ ለማዳመጥ ዝግጁ ነን!",
      contactInfo: "የመገኛ መረጃ",
      email: "ኢሜይል",
      phone: "ስልክ",
      location: "ቦታ",
      america: "Lavon, TX, USA",
      sendMessage: "መልእክት ላክ",
      nameLabel: "ስም *",
      emailLabel: "ኢሜይል *",
      subjectLabel: "ርዕስ *",
      messageLabel: "መልእክት *",
      sending: "በመላክ ላይ...",
      successTitle: "መልእክትዎ ተልኳል!",
      successMessage: "ለመጠየቅ አመሰግናለን። በቅርብ ጊዜ ከእርስዎ ጋር እንገናኛለን።",
      anotherMessage: "ሌላ መልእክት ላክ",
      benefits: [
        { icon: <Briefcase className="w-6 h-6" />, title: "ንግድዎን ያስተዋውቁ", description: "በሰሜን አሜሪካ ውስጥ በሚሊዮኖች ከሚቆጠሩ የማህበረሰባችን አባላትጋር ንግድዎን ያስተዋውቁ ወይም ይገበያዩ" },
        { icon: <UserCheck className="w-6 h-6" />, title: "ሙያዊ አገልግሎቶች", description: "የእርስዎን እውቀት እና ሙያዊ ችሎታ ከሚፈልጉ ደንበኞች ጋር ይገናኙ ወይም የእርስዎን ቋንቋና ባህል የሚያውቁ ባለሞያዎችን ያግኙ" },
        { icon: <Users className="w-6 h-6" />, title: "ማህበረሰብዎን ይቅጠሩ", description: "የራስዎን ቋንቋ እና ባህል ከሚያውቁ ሰዎችን ይቅጠሩ ወይም ከነሱ ጋር ይስሩ።" },
        { icon: <Home className="w-6 h-6" />, title: "የቤት መፍትሄዎች", description: "ከሀበሻ ማህበረሰብ ጋር ቤት ያከራዩ፣ ይሽጡ ፣ ይከራዩ ወይም ይግዙ" },
        { icon: <Calendar className="w-6 h-6" />, title: "ፕሮግራም ማስተዋወቅ", description: "ፕሮግራሞችን ፣ ዝግጅቶችን እና የማህበረሰብ ስብሰባዎችን በዚህ ድረገጽ በማስተዋወቅ ለታለመላቸው ታዳሚዎች ያድርሱ" },
        { icon: <Newspaper className="w-6 h-6" />, title: "ጠቃሚ ዜናዎችን ያጋሩ", description: "ጠቃሚና ወቅታዊ መረጃዎችን ለህብረተሰቡ ያጋሩ" }
      ],
      examples: [
        { icon: <Globe className="w-5 h-5" />, title: "የኢሚግሬሽን መረጃ", description: "ወቅታዊ የኢሚግሬሽን ፖሊሲዎች እና መረጃዎችን በማጋራት" },
        { icon: <Shield className="w-5 h-5" />, title: "የደህንነት መረጃ", description: "ጠቃሚ የደህንነት መረጃዎችን እና ለማህበረሰባችን የደህንነት ማስጠንቀቂያዎችን ማጋራት" },
        { icon: <GraduationCap className="w-5 h-5" />, title: "ስራ ፈጣሪነት", description: "የስራ ፈጠራ ዘዴዎችን ለማህበረሰባችን ማስተዋወቅ" }
      ]
    }
  };

  const t = content[language];

  return (
    <div className="w-full bg-gray-50 dark:bg-gray-900 px-4 py-16 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Hero Header */}
        <div
          className="text-center mb-16"
          data-aos="fade-up"
          data-aos-delay="100"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900 dark:text-white">
            {t.title}
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
            {t.subtitle}
          </p>
        </div>

        {/* Mission & Vision */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          <div
            className="border border-gray-200 dark:border-gray-700 rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-300 bg-white dark:bg-gray-800"
            data-aos="fade-up"
            data-aos-delay="150"
          >
            <div className="text-center pb-4">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400">
                <Target className="w-8 h-8" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{t.missionTitle}</h2>
            </div>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed text-center">
              {t.missionContent}
            </p>
          </div>

          <div
            className="border border-gray-200 dark:border-gray-700 rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-300 bg-white dark:bg-gray-800"
            data-aos="fade-up"
            data-aos-delay="200"
          >
            <div className="text-center pb-4">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400">
                <Eye className="w-8 h-8" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{t.visionTitle}</h2>
            </div>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed text-center">
              {t.visionContent}
            </p>
          </div>
        </div>

        {/* Benefits Section */}
        <div className="mb-16">
          <div
            className="text-center mb-12"
            data-aos="fade-up"
          >
            <h2 className="text-3xl font-bold mb-4 text-gray-900 dark:text-white">{t.joinTitle}</h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              {t.joinSubtitle}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {t.benefits.map((benefit, index) => (
              <div
                key={index}
                className="border border-gray-200 dark:border-gray-700 rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-300 bg-white dark:bg-gray-800 group"
                data-aos="fade-up"
                data-aos-delay={index * 50 + 100}
              >
                <div className="text-center">
                  <div className="w-12 h-12 mx-auto mb-4 rounded-lg bg-blue-50 dark:bg-gray-700 flex items-center justify-center text-blue-600 dark:text-blue-400 group-hover:scale-110 transition-transform">
                    {benefit.icon}
                  </div>
                  <h3 className="font-semibold text-lg mb-2 text-gray-900 dark:text-white">{benefit.title}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Community Information Examples */}
        <div className="mb-16">
          <div
            className="text-center mb-12"
            data-aos="fade-up"
          >
            <h2 className="text-3xl font-bold mb-4 text-gray-900 dark:text-white">{t.shareTitle}</h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              {t.shareSubtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.examples.map((example, index) => (
              <div
                key={index}
                className="border border-gray-200 dark:border-gray-700 rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-300 bg-white dark:bg-gray-800 border-l-4 border-l-blue-500"
                data-aos="fade-up"
                data-aos-delay={index * 75 + 100}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 dark:bg-gray-700 flex items-center justify-center text-blue-600 dark:text-blue-400 hover:rotate-6 transition-transform">
                    {example.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2 text-gray-900 dark:text-white">{example.title}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300">{example.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Contact Section */}
        <div
          className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-8 md:p-12 shadow-sm"
          data-aos="fade-up"
        >
          {isSuccess ? (
            <div className="text-center p-8" data-aos="zoom-in">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center text-green-600 dark:text-green-400">
                <CheckCircle className="w-10 h-10" />
              </div>
              <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">{t.successTitle}</h2>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                {t.successMessage}
              </p>
              <button
                onClick={() => {
                  setIsSuccess(false)
                }}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
              >
                {t.anotherMessage}
              </button>
            </div>
          ) : (
            <>
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4 text-gray-900 dark:text-white">{t.contactTitle}</h2>
                <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
                  {t.contactSubtitle}
                </p>
              </div>

              {submitError && (
                <div
                  className="mb-6 p-4 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 flex items-start"
                  data-aos="fade-down"
                >
                  <AlertCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                  <span>{submitError}</span>
                </div>
              )}

              <div className="grid lg:grid-cols-2 gap-12">
                {/* Contact Information */}
                <div className="space-y-6">
                  <h3 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white">{t.contactInfo}</h3>
                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-blue-50 dark:bg-gray-700 flex items-center justify-center text-blue-600 dark:text-blue-400">
                        <Mail className="w-6 h-6" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-white">{t.email}</h4>
                        <a
                          href="mailto:support@habeshanetwork.com"
                          className="text-blue-600 dark:text-blue-400 hover:underline hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                        >
                          support@habeshanetwork.com
                        </a>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-blue-50 dark:bg-gray-700 flex items-center justify-center text-blue-600 dark:text-blue-400">
                        <Phone className="w-6 h-6" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-white">{t.phone}</h4>
                        <a
                          href="tel:+18555537873"
                          className="text-blue-600 dark:text-blue-400 hover:underline hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                        >
                          +1 (855) 553-7873
                        </a>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-blue-50 dark:bg-gray-700 flex items-center justify-center text-blue-600 dark:text-blue-400">
                        <MapPin className="w-6 h-6" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-white">{t.location}</h4>
                        <p className="text-gray-600 dark:text-gray-300">{t.america}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contact Form */}
                <form
                  onSubmit={handleSubmit(onSubmit)}
                  className={`space-y-4 transition-all duration-[600ms] ease-[cubic-bezier(0.4,0,0.2,1)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
                    }`}
                  id="contact-form"
                  ref={sectionRef}
                >
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name" className="text-gray-700 dark:text-gray-300">{t.nameLabel}</Label>
                      <Input
                        id="name"
                        {...register("name", { required: language === 'en' ? "Name is required" : "ስም ያስፈልጋል" })}
                        placeholder={language === 'en' ? "Your full name" : "ሙሉ ስምዎ"}
                        className="dashboard-input"
                        aria-invalid={errors.name ? "true" : "false"}
                      />
                      {errors.name && (
                        <p className="text-sm text-red-500 flex items-center mt-1">
                          <AlertCircle className="w-4 h-4 mr-1" /> {errors.name.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-gray-700 dark:text-gray-300">{t.emailLabel}</Label>
                      <Input
                        id="email"
                        type="email"
                        {...register("email", {
                          required: language === 'en' ? "Email is required" : "ኢሜይል ያስፈልጋል",
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: language === 'en' ? "Invalid email address" : "የተሳሳተ ኢሜይል አድራሻ"
                          }
                        })}
                        placeholder={language === 'en' ? "your.email@example.com" : "ኢሜይልዎን ያስገቡ"}
                        className="dashboard-input"
                        aria-invalid={errors.email ? "true" : "false"}
                      />
                      {errors.email && (
                        <p className="text-sm text-red-500 flex items-center mt-1">
                          <AlertCircle className="w-4 h-4 mr-1" /> {errors.email.message}
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="subject" className="text-gray-700 dark:text-gray-300">{t.subjectLabel}</Label>
                    <Input
                      id="subject"
                      {...register("subject", { required: language === 'en' ? "Subject is required" : "ርዕስ ያስፈልጋል" })}
                      placeholder={language === 'en' ? "What is this about?" : "ርዕሱ ምንድን ነው?"}
                      className="dashboard-input"
                      aria-invalid={errors.subject ? "true" : "false"}
                    />
                    {errors.subject && (
                      <p className="text-sm text-red-500 flex items-center mt-1">
                        <AlertCircle className="w-4 h-4 mr-1" /> {errors.subject.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-gray-700 dark:text-gray-300">{t.messageLabel}</Label>
                    <Textarea
                      id="message"
                      {...register("message", {
                        required: language === 'en' ? "Message is required" : "መልእክት ያስፈልጋል",
                        minLength: {
                          value: 20,
                          message: language === 'en' ? "Message must be at least 20 characters" : "መልእክቱ ቢያንስ 20 ፊደላት ሊኖሩት ይገባል"
                        }
                      })}
                      placeholder={language === 'en' ? "Tell us how we can help you..." : "እንዴት እንደምንረዳዎት ይንገሩን..."}
                      className="dashboard-input min-h-[120px]"
                      aria-invalid={errors.message ? "true" : "false"}
                    />
                    {errors.message && (
                      <p className="text-sm text-red-500 flex items-center mt-1">
                        <AlertCircle className="w-4 h-4 mr-1" /> {errors.message.message}
                      </p>
                    )}
                  </div>

                  <SmsConsentCard />

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition-colors ${isSubmitting ? "opacity-75 cursor-not-allowed" : ""
                      }`}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center">
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t.sending}
                      </span>
                    ) : (
                      t.sendMessage
                    )}
                  </button>
                </form>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}